import React, { useContext } from 'react';
import { IterationContext, IterationContextType } from '../../contexts/IterationContext';
import { AddIcon, DeleteIcon } from 'assests/GmsIcons';
import LeftButton, { RightButton } from 'Components/GMS/Rules/Components/LeftButton';
import Condition from './Condition';

type ConditionGroupProps = {
    conditionNode: ConditionNode;
    position: number;
    depth?: number;
    onRemove?: () => void;
};

const ConditionGroup: React.FC<ConditionGroupProps> = ({
    conditionNode,
    position,
    depth = 0,
    onRemove
}) => {
    const isMainCondition = conditionNode.type === 'operations' && depth === 0;
    const {
        activeIterationId,
        removeCondition,
        addCondition,
        indentCondition,
        updateLogicalOperator
    } = useContext<IterationContextType>(IterationContext);

    // If it's an operation condition, render the Condition component
    if (conditionNode.type === 'operations') {
        return (
            <Condition
                conditionId={conditionNode.id}
                position={position}
                isMainCondition={isMainCondition}
            />
        );
    }

    // If it's a nested condition, render the group
    const nestedCondition = conditionNode as NestedCondition;

    const handleDelete = () => {
        if (activeIterationId && onRemove) {
            onRemove();
        } else if (activeIterationId) {
            removeCondition(activeIterationId, conditionNode.id);
        }
    };

    const handleAddCondition = () => {
        if (activeIterationId) {
            addCondition(activeIterationId, conditionNode.id);
        }
    };

    const handleIndent = (direction: 'left' | 'right') => {
        if (activeIterationId) {
            indentCondition(activeIterationId, conditionNode.id, direction);
        }
    };

    const handleLogicalOperatorChange = (subConditionId: string) => (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (activeIterationId) {
            console.log(`Updating operator for sub-condition: ${subConditionId} to ${event.target.value}`);
            updateLogicalOperator(activeIterationId, subConditionId, event.target.value as LogicalOperator);
        }
    };

    const getSubConditionOperator = (subConditionId: string): LogicalOperator => {
        const subCondition = nestedCondition.conditions.find(c => c.id === subConditionId);
        return subCondition?.logical_operator || 'AND';
    };

    const hasSubConditions = () => nestedCondition?.conditions?.length > 0;

    return (
        <div className={`tw-bg-[#FAFAFC] tw-py-6 tw-relative ${depth === 0 ? "tw-border tw-border-[#E4E4E8] " : ""}`}>
            <div className="condition-wrapper">
                <div className="left-nesting-bracket" />
                <div className="tw-flex-1">
                    {!hasSubConditions && <div className="right-indent">
                        <RightButton onClick={() => handleIndent('right')} />
                        <div className="tw-py-1 tw-bg-white"></div>
                        <LeftButton onClick={() => handleIndent('left')} />
                    </div>}

                    <div className="tw-col-span-full">
                        {nestedCondition.conditions.map((subCondition, index) => (
                            <div key={subCondition.id}>
                                {index > 0 && (
                                    <div className="tw-flex tw-justify-start tw-my-3">
                                        <select
                                            className="form-select drop-down-dm"
                                            value={getSubConditionOperator(nestedCondition.conditions[index - 1].id)}
                                            onChange={handleLogicalOperatorChange(nestedCondition.conditions[index - 1].id)}
                                            data-testid={`nested-logical-operator-${nestedCondition.conditions[index - 1].id}`}
                                            style={{
                                                textAlign: "center",
                                                fontSize: ".8rem",
                                                color: "black",
                                                fontWeight: "600",
                                                width: "80px"
                                            }}
                                        >
                                            <option value="AND">And</option>
                                            <option value="OR">Or</option>
                                        </select>
                                    </div>
                                )}
                                <ConditionGroup
                                    conditionNode={subCondition}
                                    position={index}
                                    depth={depth + 1}
                                    onRemove={() => {
                                        if (activeIterationId) {
                                            removeCondition(activeIterationId, subCondition.id);
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="right-nesting-bracket" />

                {!hasSubConditions && <div className="mt-1 delete-btn-cba d-flex align-items-center">
                    <div>
                        <button
                            className="delete-button"
                            onClick={handleDelete}
                            data-testid={`delete-condition-${conditionNode.id}`}
                        >
                            <DeleteIcon />
                        </button>
                    </div>
                </div>}
            </div>

            {depth === 0 && <button
                className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-[#0747A6] tw-rounded-md hover:tw-bg-blue-50 tw-mt-2"
                onClick={handleAddCondition}
                data-testid={`add-condition-${conditionNode.id}`}
            >
                <AddIcon color='#0747A6' />
                <span className="tw-text-[#0747A6] tw-text-sm">Add Condition</span>
            </button>}
        </div>
    );
};

export default ConditionGroup;